import { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import CurrencyInput from '../components/CurrencyInput';

const fmt = (n, sign = false) => {
  const s = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(n));
  if (sign) return (n >= 0 ? '+' : '−') + s;
  return s;
};

const monthStart = (d) => format(startOfMonth(d), 'yyyy-MM-dd');
const monthEnd   = (d) => format(endOfMonth(d), 'yyyy-MM-dd');
const monthLabel = (d) => format(d, 'MMMM yyyy');

function ReconciliationCard({ entry, onUpdate, onDelete }) {
  const [editingEop, setEditingEop] = useState(false);
  const isMobile = useIsMobile();
  const [eopVal, setEopVal] = useState(entry.actual_eop_cash ?? '');
  const [saving, setSaving] = useState(false);

  const saveEop = async () => {
    setSaving(true);
    try {
      await onUpdate(entry.id, { actual_eop_cash: parseFloat(eopVal) });
      setEditingEop(false);
      toast.success('EoP balance saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const isReconciled = entry.is_reconciled;
  const hasDelta = entry.delta !== null;
  const deltaColor = isReconciled ? 'var(--green)' : entry.delta > 0 ? 'var(--green)' : 'var(--red)';

  return (
    <div className="card animate-in" style={{
      borderLeft: `3px solid ${isReconciled ? 'var(--green)' : hasDelta ? 'var(--red)' : 'var(--border)'}`,
    }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{entry.period_label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {format(new Date(entry.period_start), 'MMM d')} → {format(new Date(entry.period_end), 'MMM d, yyyy')}
            {' · '}{entry.transaction_count} transactions
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {isReconciled && <span className="badge badge-green">✓ Reconciled</span>}
          {hasDelta && !isReconciled && <span className="badge badge-red">! {fmt(entry.delta, true)} gap</span>}
          {!hasDelta && <span className="badge badge-yellow">Pending EoP</span>}
          <button className="btn btn-sm btn-danger" onClick={() => onDelete(entry.id)}>×</button>
        </div>
      </div>

      {/* Cash flow waterfall */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 12 }}>Cash Flow Waterfall</div>

        {[
          { label: 'Beginning of Period Cash', value: entry.bop_cash, color: 'var(--text-primary)', bold: true, prefix: '' },
          { label: '+ Inflows (income, credits)', value: entry.inflows, color: 'var(--green)', prefix: '+' },
          { label: '− Outflows (expenses)', value: -entry.outflows, color: 'var(--red)', prefix: '−' },
          { label: '− Investment cash out (buys)', value: -entry.inv_cash_out, color: 'var(--red)', prefix: '−', skip: entry.inv_cash_out === 0 },
          { label: '+ Investment proceeds (sells)', value: entry.inv_cash_in, color: 'var(--green)', prefix: '+', skip: entry.inv_cash_in === 0 },
        ].filter(r => !r.skip).map((row, i, arr) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 0',
            borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <span style={{ fontSize: 13, color: row.bold ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: row.bold ? 600 : 400 }}>
              {row.label}
            </span>
            <span style={{ fontSize: 13, fontWeight: row.bold ? 600 : 500, color: row.bold ? 'var(--text-primary)' : row.color }}>
              {row.value >= 0 ? row.prefix : '−'}{fmt(row.value)}
            </span>
          </div>
        ))}

        {/* Divider */}
        <div style={{ borderTop: '2px solid var(--border-bright)', marginTop: 8, paddingTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>= Expected End of Period Cash</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{fmt(entry.expected_eop_cash)}</span>
          </div>
        </div>
      </div>

      {/* EoP actual entry */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, padding: '16px 20px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>Actual Bank Balance Check</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Actual EoP cash (from your bank)</div>
            {editingEop ? (
              <div className="flex gap-2">
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  placeholder="Enter your actual balance"
                  value={eopVal}
                  onChange={e => setEopVal(e.target.value)}
                  autoFocus
                  style={{ maxWidth: 200 }}
                />
                <button className="btn btn-primary btn-sm" onClick={saveEop} disabled={saving}>Save</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingEop(false)}>Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setEditingEop(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
              >
                {entry.actual_eop_cash != null
                  ? <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(entry.actual_eop_cash)} <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>(click to edit)</span></span>
                  : <span style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'underline' }}>+ Enter actual balance</span>
                }
              </button>
            )}
          </div>

          {hasDelta && (
            <div style={{ textAlign: 'right', minWidth: 140 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Difference</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: deltaColor }}>
                {entry.delta === 0 ? '✓ $0.00' : fmt(entry.delta, true)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {isReconciled ? 'Perfect match!' : entry.delta > 0 ? 'Unrecorded income' : 'Missing expenses'}
              </div>
            </div>
          )}
        </div>
      </div>

      {entry.notes && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Note: {entry.notes}
        </div>
      )}
    </div>
  );
}

export default function ReconciliationPage() {
  const [entries, setEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile();

  const defaultPeriod = subMonths(new Date(), 1);
  const [form, setForm] = useState({
    period_label: monthLabel(defaultPeriod),
    period_start: monthStart(defaultPeriod),
    period_end: monthEnd(defaultPeriod),
    bop_cash: '',
    actual_eop_cash: '',
    notes: '',
  });
  const [usdBop, setUsdBop] = useState(0);
  const [usdEop, setUsdEop] = useState(0);

  const load = async () => {
    try {
      const data = await api.getReconciliations();
      setEntries(data);
    } catch (e) {
      toast.error('Failed to load reconciliations');
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-fill label when dates change
  const setDateField = (k, v) => {
    setForm(f => {
      const updated = { ...f, [k]: v };
      // Auto-generate label from period_start
      if (k === 'period_start' && v) {
        try {
          updated.period_label = format(new Date(v + 'T00:00:00'), 'MMMM yyyy');
        } catch {}
      }
      return updated;
    });
  };

  const save = async () => {
    if (!form.bop_cash) return toast.error('Opening balance is required');
    setSaving(true);
    try {
      await api.createReconciliation({
        period_label: form.period_label,
        period_start: form.period_start,
        period_end: form.period_end,
        bop_cash: usdBop > 0 ? usdBop : parseFloat(form.bop_cash),
        actual_eop_cash: form.actual_eop_cash ? (usdEop > 0 ? usdEop : parseFloat(form.actual_eop_cash)) : null,
        notes: form.notes || null,
      });
      toast.success('Reconciliation created!');
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, body) => {
    const updated = await api.updateReconciliation(id, body);
    setEntries(es => es.map(e => e.id === id ? updated : e));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reconciliation period?')) return;
    await api.deleteReconciliation(id);
    setEntries(es => es.filter(e => e.id !== id));
    toast.success('Deleted');
  };

  const reconciledCount = entries.filter(e => e.is_reconciled).length;
  const pendingCount = entries.filter(e => !e.is_reconciled).length;

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Reconciliation</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>
            Cross-check your BoP → EoP cash flow to make sure nothing is missing
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Period</button>
      </div>

      {entries.length > 0 && (
        <div className="grid-3 mb-6">
          <div className="card">
            <div className="stat-label">Periods Tracked</div>
            <div className="stat-value gold">{entries.length}</div>
          </div>
          <div className="card">
            <div className="stat-label">Reconciled ✓</div>
            <div className="stat-value green">{reconciledCount}</div>
          </div>
          <div className="card">
            <div className="stat-label">Pending / Gaps</div>
            <div className="stat-value" style={{ color: pendingCount > 0 ? 'var(--red)' : 'var(--text-muted)' }}>{pendingCount}</div>
          </div>
        </div>
      )}

      {/* How it works callout */}
      {entries.length === 0 && (
        <div className="card mb-6" style={{ borderStyle: 'dashed', padding: '40px' }}>
          <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>=</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>How reconciliation works</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              For each period, you enter your <strong style={{ color: 'var(--text-primary)' }}>opening bank balance (BoP)</strong>.
              The app computes: BoP + all inflows − all expenses − investment cash out = <strong style={{ color: 'var(--accent)' }}>expected closing balance</strong>.
              You then enter your <strong style={{ color: 'var(--text-primary)' }}>actual bank balance (EoP)</strong>, and if the delta is $0.00 — you're perfectly reconciled.
              Any gap means there are transactions you haven't recorded yet.
            </div>
            <button className="btn btn-primary mt-4" onClick={() => setShowModal(true)}>Start with last month</button>
          </div>
        </div>
      )}

      {/* Reconciliation cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {entries.map(entry => (
          <ReconciliationCard
            key={entry.id}
            entry={entry}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-title">New Reconciliation Period</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
              Define the period and enter your opening bank balance. You can enter the actual closing balance now or later.
            </div>

            <div className="form-group">
              <label className="form-label">Period Label</label>
              <input className="input" value={form.period_label} onChange={e => setForm(f => ({ ...f, period_label: e.target.value }))} placeholder="e.g. January 2026" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Period Start</label>
                <input className="input" type="date" value={form.period_start} onChange={e => setDateField('period_start', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Period End</label>
                <input className="input" type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginTop: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">BoP Cash — Opening Balance *</label>
                <CurrencyInput
                  value={form.bop_cash}
                  onChange={(raw, usd) => { setForm(f => ({ ...f, bop_cash: raw })); setUsdBop(usd); }}
                  placeholder="Bank balance at start of period"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">EoP Cash — Closing Balance (optional)</label>
                <CurrencyInput
                  value={form.actual_eop_cash}
                  onChange={(raw, usd) => { setForm(f => ({ ...f, actual_eop_cash: raw })); setUsdEop(usd); }}
                  placeholder="Fill in after period ends"
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="form-label">Notes (optional)</label>
              <input className="input" placeholder="Any context for this period" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            {/* Quick preview */}
            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.bop_cash}>
                {saving ? 'Creating…' : 'Create Period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
